<script lang="ts">
  import type { Snippet } from 'svelte';

  interface Props {
    children: Snippet;
    theme?: 'light' | 'dark';
    url?: string;
    secure?: boolean;
    class?: string;
    flat?: boolean;
  }

  let {
    children,
    theme = 'light',
    url = 'apple.com',
    secure = true,
    class: className = '',
    flat = false,
  }: Props = $props();

  // Palette derived from Figma file Jqut30KYsC5B3xvEwgJp9L, nodes 2:4708 (light) and 2:4576 (dark).
  const palette = $derived(
    theme === 'dark'
      ? {
          border: '#1f1f1f',
          headerBg: '#3a3a3a',
          urlBg: '#535355',
          urlText: '#e8e8e8',
          icon: '#cfcfcf',
          contentBg: '#414141',
          baseBorder: '#2a2a2a',
        }
      : {
          border: '#d3d3d3',
          headerBg: '#f6f6f6',
          urlBg: '#dadada',
          urlText: '#1d1d1f',
          icon: '#5e5e5e',
          contentBg: '#f4f4f4',
          baseBorder: '#e0e0e0',
        }
  );
</script>

<!--
  SafariFrame — macOS Safari chrome with traffic-light buttons (top-left).
  Rebuilt as CSS+SVG from Figma file Jqut30KYsC5B3xvEwgJp9L (browser node 2:4708/2:4576).
  Single chrome row: traffic lights · sidebar/back · centered URL pill · downloads/share/+.
-->
<div
  class="safari-frame {className}"
  class:safari-frame--flat={flat}
  style="--sf-border: {palette.border}; --sf-header-bg: {palette.headerBg}; --sf-url-bg: {palette.urlBg}; --sf-url-text: {palette.urlText}; --sf-icon: {palette.icon}; --sf-content-bg: {palette.contentBg}; --sf-base-border: {palette.baseBorder};"
>
  <div class="safari-header">
    <!-- Traffic lights (left) -->
    <div class="safari-lights">
      <span class="safari-light safari-light--close"></span>
      <span class="safari-light safari-light--min"></span>
      <span class="safari-light safari-light--max"></span>
    </div>

    <!-- Sidebar + dropdown + back -->
    <div class="safari-leading">
      <span class="safari-icon" aria-hidden="true">
        <svg viewBox="0 0 18 18" width="16" height="16">
          <rect x="2" y="3.5" width="14" height="11" rx="2" fill="none" stroke="var(--sf-icon)" stroke-width="1.2" />
          <line x1="6.5" y1="3.5" x2="6.5" y2="14.5" stroke="var(--sf-icon)" stroke-width="1.2" />
        </svg>
      </span>
      <span class="safari-icon" aria-hidden="true">
        <svg viewBox="0 0 18 18" width="10" height="10">
          <path d="M5 7l4 4 4-4" stroke="var(--sf-icon)" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" fill="none" />
        </svg>
      </span>
      <span class="safari-icon" aria-hidden="true">
        <svg viewBox="0 0 18 18" width="14" height="14">
          <path d="M11 4l-5 5 5 5" stroke="var(--sf-icon)" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" fill="none" />
        </svg>
      </span>
    </div>

    <!-- Centered URL pill -->
    <div class="safari-url-wrap">
      <div class="safari-url">
        <span class="safari-url-favicon" aria-hidden="true"></span>
        <span class="safari-url-text">{url}</span>
        {#if secure}
          <span class="safari-url-lock" aria-hidden="true">
            <svg viewBox="0 0 18 18" width="11" height="11">
              <rect x="4.5" y="8" width="9" height="6" rx="1" fill="var(--sf-icon)" />
              <path d="M6 8V5.75a3 3 0 0 1 6 0V8" stroke="var(--sf-icon)" stroke-width="1.2" fill="none" stroke-linecap="round" />
            </svg>
          </span>
        {/if}
      </div>
      <button class="safari-tab-overflow" type="button" aria-label="Tab overview">
        <svg viewBox="0 0 18 18" width="14" height="14" aria-hidden="true">
          <circle cx="5.5" cy="9" r="0.9" fill="var(--sf-icon)" />
          <circle cx="9" cy="9" r="0.9" fill="var(--sf-icon)" />
          <circle cx="12.5" cy="9" r="0.9" fill="var(--sf-icon)" />
        </svg>
      </button>
    </div>

    <!-- Trailing icons -->
    <div class="safari-trailing">
      <span class="safari-icon" aria-hidden="true">
        <svg viewBox="0 0 18 18" width="16" height="16">
          <circle cx="9" cy="9" r="6.5" fill="none" stroke="var(--sf-icon)" stroke-width="1.2" />
          <path d="M9 5v6m0 0l-2.5-2.5M9 11l2.5-2.5" stroke="var(--sf-icon)" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" fill="none" />
        </svg>
      </span>
      <span class="safari-icon" aria-hidden="true">
        <svg viewBox="0 0 18 18" width="14" height="14">
          <path d="M9 2v9M9 2L6.5 4.5M9 2l2.5 2.5" stroke="var(--sf-icon)" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" fill="none" />
          <path d="M4 9.5V14a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V9.5" stroke="var(--sf-icon)" stroke-width="1.3" fill="none" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </span>
      <span class="safari-icon" aria-hidden="true">
        <svg viewBox="0 0 18 18" width="14" height="14">
          <path d="M9 4v10M4 9h10" stroke="var(--sf-icon)" stroke-width="1.4" stroke-linecap="round" />
        </svg>
      </span>
    </div>
  </div>

  <div class="safari-content">
    {@render children()}
  </div>
</div>

<style>
  .safari-frame {
    position: relative;
    width: 100%;
    border: 1px solid var(--sf-border);
    border-radius: 10px;
    background: var(--sf-content-bg);
    overflow: hidden;
    box-shadow:
      0 0 8px rgba(0, 0, 0, 0.18),
      0 32px 64px rgba(0, 0, 0, 0.22);
    display: flex;
    flex-direction: column;
    font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  }

  .safari-frame--flat {
    box-shadow: none;
  }

  .safari-header {
    flex: 0 0 auto;
    height: 52px;
    background: var(--sf-header-bg);
    border-bottom: 1px solid var(--sf-base-border);
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 16px;
    position: relative;
  }

  .safari-lights {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }

  .safari-light {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    display: inline-block;
  }

  .safari-light--close {
    background: #ff5f57;
  }

  .safari-light--min {
    background: #febc2e;
  }

  .safari-light--max {
    background: #28c840;
  }

  .safari-leading {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }

  .safari-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .safari-url-wrap {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 0;
    position: relative;
  }

  .safari-url {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: var(--sf-url-bg);
    border-radius: 6px;
    height: 28px;
    padding: 0 12px;
    color: var(--sf-url-text);
    font-size: 12px;
    min-width: 320px;
    max-width: 100%;
    justify-content: center;
  }

  .safari-url-favicon {
    width: 13px;
    height: 13px;
    background: var(--sf-icon);
    border-radius: 2px;
    flex-shrink: 0;
    opacity: 0.85;
  }

  .safari-url-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .safari-url-lock {
    display: inline-flex;
    flex-shrink: 0;
    opacity: 0.75;
  }

  .safari-tab-overflow {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 28px;
    height: 28px;
    border: 0;
    background: var(--sf-url-bg);
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: default;
    padding: 0;
  }

  .safari-trailing {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-shrink: 0;
    margin-left: 36px; /* leave room for the tab-overflow button */
  }

  .safari-content {
    flex: 1;
    min-height: 0;
    background: var(--sf-content-bg);
    overflow: hidden;
    position: relative;
  }
</style>
